import { useCurrentUserGroups } from "@admin/hooks/useCurrentUserGroups";
import {
  ControlPeriod,
  ControlPeriods,
  ControlPeriodType,
  dateToISO,
  FoodControlPointInstance,
  FoodReport,
  InstanceStatus,
  isDisplayNameTypes,
  useControlPointTypeTranslations,
} from "@enos-monorepo/lib";
import { isSameDay } from "date-fns";

import { ReportInfo } from "./ReportInfo";

interface ReportTableProps {
  report: FoodReport[];
  title: string;
  selectedMonth?: Date;
  onCellClick?: (params: {
    instances: FoodControlPointInstance[];
    report: FoodReport;
    date: Date;
  }) => void;
}

export function ReportTable({
  report,
  title,
  selectedMonth,
  onCellClick,
}: ReportTableProps) {
  const controlPointTypeTranslations = useControlPointTypeTranslations();
  const { isSuperAdmin } = useCurrentUserGroups();

  const getDatesInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth(); // Ay indeksi 0'dan başlar, yani Ocak = 0
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    return Array.from({ length: daysInMonth }, (_, i) => {
      return new Date(year, month, i + 1);
    });
  };

  const daysInMonth = selectedMonth
    ? getDatesInMonth(selectedMonth)
    : getDatesInMonth(new Date());

  const isWeekend = (date: Date) => {
    const day = date.getDay(); // 0 = Pazar, 6 = Cumartesi
    return day === 0 || day === 6;
  };

  const calculateControlDates = (
    controlPeriod: ControlPeriod | undefined,
    startDate: Date,
  ) => {
    if (!controlPeriod) return [];

    const dates = [];
    const current = new Date(startDate);

    const getISOWeekNumber = (date: Date): number => {
      const tempDate = new Date(date.getTime());
      tempDate.setUTCDate(
        tempDate.getUTCDate() + 4 - (tempDate.getUTCDay() || 7),
      );
      const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
      return Math.ceil(
        ((tempDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
      );
    };

    if (controlPeriod.description === ControlPeriodType.weekly) {
      // Haftalık kontroller için her haftanın pazarını ekle
      while (current <= daysInMonth[daysInMonth.length - 1]) {
        if (current.getDay() === 0) {
          dates.push(new Date(current)); // Pazar
        }
        current.setDate(current.getDate() + 1);
      }
    }

    if (controlPeriod.description === ControlPeriodType.twoWeekly) {
      // Yılın çift haftalarındaki pazarlar
      while (current <= daysInMonth[daysInMonth.length - 1]) {
        if (current.getDay() === 0) {
          const weekNumber = getISOWeekNumber(current);
          if (weekNumber % 2 === 0) {
            dates.push(new Date(current));
          }
        }
        current.setDate(current.getDate() + 1);
      }
    }

    // Aylık kontroller için ayın son günü
    if (controlPeriod.description === ControlPeriodType.monthly) {
      dates.push(daysInMonth[daysInMonth.length - 1]);
    }

    // Yıllık kontroller için yılın son günü
    if (controlPeriod.description === ControlPeriodType.yearly) {
      const yearEnd = new Date(
        startDate.getFullYear(),
        11, // Aralık
        31,
      );
      dates.push(yearEnd);
    }

    return dates;
  };

  const hasReportsInBackPeriod = (
    day: Date,
    duration: number,
    foodControlPointInstances: FoodControlPointInstance[],
  ) => {
    const startDate = new Date(day);
    startDate.setDate(startDate.getDate() - duration + 1);

    return foodControlPointInstances.some((fc) => {
      if (!fc.updatedDate) return false;
      const endDate = new Date(fc.updatedDate);
      return endDate >= startDate && endDate <= day;
    });
  };

  if (report.length === 0) return null;

  return (
    <div className="card min-w-full card-table scrollable-x-auto">
      <table className="table align-middle table-auto table-border table-xs ">
        <thead>
          <tr>
            <th style={{ width: 200, paddingTop: 23, paddingBottom: 23 }}>
              {title}
            </th>
            {daysInMonth.map((day) => (
              <th
                key={day.toString()}
                style={{
                  textAlign: "center",
                  backgroundColor: isWeekend(day) ? "#EFF6FF" : undefined,
                  padding: 0,
                }}
              >
                {day.getDate()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.map((row, rowIndex) => {
            return (
              <tr key={rowIndex}>
                <td>
                  {!isDisplayNameTypes(row.type)
                    ? controlPointTypeTranslations[row.type]
                    : row.name}
                </td>
                {daysInMonth.map((day, index) => {
                  const filteredInstances = row.foodControlPointInstances
                    .filter(
                      (fc) =>
                        fc.updatedDate &&
                        isSameDay(dateToISO(day), fc.updatedDate),
                    )
                    .filter(
                      (fi) =>
                        fi.status !== InstanceStatus.expired &&
                        fi.status !== InstanceStatus.waiting,
                    );
                  const controlDates = calculateControlDates(
                    row.controlPeriod ?? ControlPeriods["1 Week"],
                    daysInMonth[0],
                  );

                  let isEndPeriod = controlDates.some((controlDate) =>
                    isSameDay(controlDate, day),
                  );

                  const endPeriodDisplay = isEndPeriod
                    ? hasReportsInBackPeriod(
                        day,
                        row.controlPeriod?.durationInDays ??
                          ControlPeriods["1 Week"].durationInDays,
                        row.foodControlPointInstances.filter(
                          (fp) => fp.status === InstanceStatus.completed,
                        ),
                      )
                    : undefined;

                  isEndPeriod = endPeriodDisplay ? false : isEndPeriod;

                  return (
                    <td
                      key={day.toDateString()}
                      className="!py-2.5 !px-1.5 !w-0 "
                      style={{
                        backgroundColor: isWeekend(day) ? "#EFF6FF" : undefined,
                        paddingRight: 0,
                      }}
                    >
                      <ReportInfo
                        disabled={
                          (day > new Date() || !isSuperAdmin) &&
                          filteredInstances.length === 0
                        }
                        isEndPeriod={isEndPeriod}
                        instances={filteredInstances}
                        onClick={() => {
                          onCellClick?.({
                            instances: filteredInstances,
                            report: row,
                            date: day,
                          });
                        }}
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
